import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Mesh } from '~lmo/models/mesh.model';
import { FracClusterPurchaseOrder } from '~lmo/models/purchaseOrders.model';
import { LmoConstantsService } from '~lmo/services/lmo-constants.service';
import { LmoFracFcpoService } from '~lmo/services/lmo-frac-fcpo.service';
import { trackById } from '~utilities/trackById';

interface MeshWithFcpos extends Mesh {
  fcpos: FracClusterPurchaseOrder[];
  allocation: number;
  ordered: number;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-frac-fcpo-manage',
  styleUrls: ['./lmo-frac-fcpo-manage.component.scss'],
  templateUrl: './lmo-frac-fcpo-manage.component.html',
})
export class LmoFracFcpoManageComponent implements OnInit {
  public trackById = trackById;
  public meshesWithFcpos$: Observable<MeshWithFcpos[]>;
  constructor(private fcpoService: LmoFracFcpoService, private constantsService: LmoConstantsService) {}

  public ngOnInit() {
    this.meshesWithFcpos$ = combineLatest(this.fcpoService.currentFracFcpos$, this.constantsService.meshesById$).pipe(
      map(mergeMeshWithFcpo),
      tap(sortMeshesWithFcpos),
      tap(addTotalsToMeshes),
    );
  }
}

function mergeMeshWithFcpo([fcpos, meshes]: [FracClusterPurchaseOrder[], Mesh[]]): MeshWithFcpos[] {
  const headerRecord: Record<number, FracClusterPurchaseOrder[]> = fcpos.reduce(
    (record: Record<number, FracClusterPurchaseOrder[]>, fcpo) => {
      if (!record[fcpo.purchaseOrder.meshId]) {
        record[fcpo.purchaseOrder.meshId] = [];
      }
      record[fcpo.purchaseOrder.meshId].push(fcpo);
      return record;
    },
    {},
  );
  const meshHeaders: MeshWithFcpos[] = Object.keys(headerRecord).map((key) => ({
    ...meshes[key],
    fcpos: headerRecord[key],
  }));
  return meshHeaders;
}

function sortMeshesWithFcpos(meshesWithFcpos: MeshWithFcpos[]): void {
  meshesWithFcpos.forEach((header) => {
    header.fcpos.sort(sortByPreferredOrder);
  });
  meshesWithFcpos.sort(sortByPreferredOrder);
}

function addTotalsToMeshes(meshesWithFcpos: MeshWithFcpos[]): void {
  meshesWithFcpos.forEach((meshWithFcpo) => {
    meshWithFcpo.allocation = meshWithFcpo.fcpos
      .map((fcpo) => fcpo.purchaseOrder.allocation)
      .reduce((sum, allocation) => sum + allocation, 0);
    meshWithFcpo.ordered = meshWithFcpo.fcpos
      .map((fcpo) => fcpo.purchaseOrder.ordered || 0)
      .reduce((sum, allocation) => sum + allocation, 0);
  });
}

function sortByPreferredOrder<T extends { preferredOrder: number }>(a: T, b: T): number {
  return a.preferredOrder < b.preferredOrder ? -1 : 1;
}
